import React, { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Form,
    Input,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import Spinner from "../../Components/Common/Spinner";
import NoDataFound from "../../Components/Common/NoDataFound";
import { customStyles } from "../../Constant";
import "react-toastify/dist/ReactToastify.css";
import { Edit2, Trash2 } from "react-feather";

const PAGE_SIZES = [10, 25, 50, 100];

const operationStringToArray = (str) => {
    if (!str || typeof str !== "string") return [];
    return str.split("|").map((s) => s.trim()).filter(Boolean);
};

const ManageModules = () => {
    const navigate = useNavigate();
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    const isSuperDenied = userData.is_super === 0 || userData.is_super === "0";

    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchApplied, setSearchApplied] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [deletingId, setDeletingId] = useState(null);

    const fetchModules = useCallback(async () => {
        try {
            const res = await AxiosInstance().post("/admin/module/get_modules", {});
            if (res.data.status && res.data.data) setModules(res.data.data);
        } catch (e) {
            console.error(e);
            toast.error(e.response?.data?.message || "Failed to load modules");
        }
    }, []);

    useEffect(() => {
        if (isSuperDenied) {
            setLoading(false);
            setError("Permission denied. Super Admin only.");
            return;
        }
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                await fetchModules();
            } catch (e) {
                const msg = e.response?.data?.message || (e.response?.status === 403 ? "Permission denied. Super Admin only." : "Failed to load data");
                setError(msg);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [isSuperDenied, fetchModules]);

    const filteredModules = useMemo(() => {
        if (!searchApplied.trim()) return modules;
        const q = searchApplied.toLowerCase().trim();
        return modules.filter(
            (m) =>
                (m.module_name || "").toLowerCase().includes(q) ||
                (m.controller_name || "").toLowerCase().includes(q) ||
                (m.fa_icon || "").toLowerCase().includes(q)
        );
    }, [modules, searchApplied]);

    const openCreate = () => navigate("/job/admin/manage-modules/new");
    const handleSearchSubmit = (e) => {
        e?.preventDefault();
        setSearchApplied(searchQuery);
    };
    const handlePageChange = (page) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const openEdit = (module) => {
        navigate("/job/admin/manage-modules/new", { state: { editingModule: module } });
    };

    const handleDelete = async (module) => {
        if (!window.confirm(`Delete module "${module.module_name}"? This cannot be undone.`)) return;
        setDeletingId(module.module_id);
        try {
            const res = await AxiosInstance().post(`/admin/module/delete/${module.module_id}`, {});
            if (res.data.status) {
                toast.success(res.data.message || "Module deleted.");
                fetchModules();
            } else {
                toast.error(res.data.message || "Delete failed.");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Delete failed.");
        } finally {
            setDeletingId(null);
        }
    };

    const tableColumns = [
        {
            name: "#",
            cell: (row, index) => (currentPage - 1) * perPage + index + 1,
            width: "60px",
            sortable: false,
        },
        {
            name: "Module Name",
            selector: (row) => row.module_name || "—",
            sortable: true,
            grow: 1.5,
            cell: (row) => <span className="fw-medium">{row.module_name || "—"}</span>,
        },
        {
            name: "Controller Name",
            selector: (row) => row.controller_name || "—",
            sortable: true,
            grow: 1.5,
        },
        {
            name: "Icon",
            width: "140px",
            cell: (row) =>
                row.fa_icon ? (
                    <span>
                        <i className={`fa ${row.fa_icon} me-1`} />
                        <span className="small text-muted">{row.fa_icon}</span>
                    </span>
                ) : (
                    <span className="text-muted">—</span>
                ),
        },
        {
            name: "Operations",
            grow: 1,
            cell: (row) => (
                <span className="small">{(operationStringToArray(row.operation) || []).join(", ") || "—"}</span>
            ),
        },
        {
            name: "Sort Order",
            width: "100px",
            center: true,
            selector: (row) => row.sort_order,
        },
        {
            name: "Actions",
            sortable: false,
            width: "100px",
            center: true,
            cell: (row) => (
                <div className="d-flex justify-content-center align-items-center gap-1">
                    <button
                        type="button"
                        className="btn btn-link p-0 border-0 bg-transparent text-primary"
                        onClick={() => openEdit(row)}
                        title="Edit Module"
                    >
                        <Edit2 size={18} className="edit-icon" />
                    </button>
                    <button
                        type="button"
                        className="btn btn-link p-0 text-danger border-0 bg-transparent"
                        onClick={() => handleDelete(row)}
                        disabled={deletingId === row.module_id}
                        title="Delete Module"
                    >
                        <Trash2 size={18} className="delete-icon" />
                    </button>
                </div>
            ),
        },
    ];

    if (isSuperDenied && !loading) {
        return (
            <Fragment>
                <Breadcrumbs title="Manage Modules" mainTitle="Module Management" />
                <Row>
                    <Col xs="12">
                        <Card className="border-0 shadow-sm">
                            <CardBody className="text-center py-5">
                                <i className="fa fa-lock fa-3x text-warning mb-3" />
                                <h5 className="text-danger mb-2">Access Denied</h5>
                                <p className="text-muted mb-0">Only Super Admin can manage modules.</p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <ToastContainer />
            </Fragment>
        );
    }

    if (loading && modules.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Manage Modules" mainTitle="Module Management" />
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner />
                </div>
            </Fragment>
        );
    }

    if (error && modules.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Manage Modules" mainTitle="Module Management" />
                <Row>
                    <Col xs="12">
                        <Card>
                            <CardBody className="text-center py-5">
                                <p className="text-danger mb-0">{error}</p>
                                <Button color="primary" className="mt-3" onClick={() => window.location.reload()}>
                                    Retry
                                </Button>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <ToastContainer />
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs title="Manage Modules" mainTitle="Module Management" />
            <Row>
                <Col xs="12" className="box-col-12 xl-100">
                    <Card>
                        <CardHeader className="py-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
                                <Form className="theme-form me-2 flex-grow-1" onSubmit={handleSearchSubmit}>
                                    <div className="input-group flex-nowrap" style={{ height: 35, maxWidth: "350px" }}>
                                        <Input
                                            className="form-control"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            type="search"
                                            placeholder="Search by module name, controller or icon..."
                                        />
                                        <Button color="primary" type="submit" className="btn-primary input-group-text pe-3 ps-3">
                                            Search
                                        </Button>
                                    </div>
                                </Form>
                                <div className="d-flex align-items-center ms-auto mt-3 mt-md-0 gap-2">
                                    <Button color="primary" onClick={openCreate} className="d-flex align-items-center">
                                        <i className="fa fa-plus me-1" /> Add Module
                                    </Button>
                                    <Button color="primary" outline onClick={fetchModules} className="d-flex align-items-center">
                                        <i className="fa fa-sync-alt me-1" /> Refresh
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>

                        <CardBody className="px-0 py-3">
                            <div className="table-container">
                                <DataTable
                                    responsive
                                    customStyles={customStyles}
                                    data={filteredModules}
                                    columns={tableColumns}
                                    striped
                                    pagination
                                    paginationServer={false}
                                    paginationTotalRows={filteredModules.length}
                                    paginationPerPage={perPage}
                                    paginationRowsPerPageOptions={PAGE_SIZES}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    progressPending={loading}
                                    progressComponent={<Spinner />}
                                    noDataComponent={<NoDataFound message="No modules found" />}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <ToastContainer />
        </Fragment>
    );
};

export default ManageModules;
